import React from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import {
  ActionIconCheckmark,
  SelectorLogo,
  SelectorDirect,
  SpacerHorizontal,
  SpacerVertical,
  Typography,
} from '@klarna/bubble-ui';

import { translator } from 'mage-i18n';
import {
  Alert,
  defaultContainerPropTypes,
  HorizontalWrapper,
  payloadUtils,
  Row,
  Column,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CY_PRE_ASSESSMENT_PRODUCTS_CONTAINER_IDS } from './constants';
import { CAPS } from '@/constants';
import { CallToActionButton, ContainerTemplate, BackButton, Flag } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getDefaultState } from './selectors';
import { getProductNames, getSellingCountries } from '@/selectors/options';
import { preAssessmentDetails } from '@/selectors/merchant';
const tPrefix = 'products-fe.containers.pre_assessment_products';

const {
  BTN_SUBMIT,
  BTN_BACK,
  SELECT_COUNTRY,
  SELECT_PRODUCT,
  SELECTED_PRODUCT_BATCH,
} = CY_PRE_ASSESSMENT_PRODUCTS_CONTAINER_IDS;

const ChannelCheckbox = ({ onClickFn, productKey, channel, checked, children }) => (
  <Typography
    textToken="text-style/text/super-paragraphs/body/medium"
    colorToken="colors/text/neutral"
  >
    <input
      id={`checkbox-${productKey}-${channel}`}
      type="checkbox"
      checked={checked}
      value={channel}
      onClick={onClickFn(productKey, channel)}
      style={{ position: 'relative', top: 1, marginRight: 5 }}
    />
    <label onClick={onClickFn(productKey, channel)}>{children}</label>
  </Typography>
);

ChannelCheckbox.propTypes = {
  channel: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  children: PropTypes.string.isRequired,
  productKey: PropTypes.string.isRequired,
  onClickFn: PropTypes.func.isRequired,
};

export class PreAssessmentProducts extends React.Component {
  constructor(props) {
    super(props);

    this.onSelect = this.onSelect.bind(this);
    this.onChangeCountry = this.onChangeCountry.bind(this);
    this.onClickCheckbox = this.onClickCheckbox.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.onNext = this.onNext.bind(this);

    const {
      defaultState: { productsPerCountry },
      preAssessmentDetails,
    } = props;

    const { sellingCountries = { all: [] } } = preAssessmentDetails;

    this.state = {
      currentCountry: sellingCountries.all[0],
      productsPerCountry,
    };
  }

  onChangeCountry(country) {
    this.setState({ currentCountry: country });
  }

  onClickCheckbox(key, value) {
    return evt => {
      evt.stopPropagation();
      const { currentCountry } = this.state;

      const { [key]: currentProduct, ...currentProducts } = _get(
        this.state,
        `productsPerCountry.${currentCountry}`,
        {},
      );

      const futureChannels =
        currentProduct === undefined || !currentProduct.includes(value)
          ? (currentProduct || []).concat(value)
          : currentProduct.filter(v => v !== value) || [];

      this.setState({
        productsPerCountry: {
          ...this.state.productsPerCountry,
          [currentCountry]: {
            ...currentProducts,
            ...(futureChannels.length > 0 ? { [key]: futureChannels } : {}),
          },
        },
      });
    };
  }

  getOptions(country) {
    const { productNames } = this.props;
    const { productsPerCountry } = this.state;

    return productNames.reduce((all, product) => {
      const sellingCountries = _get(product, 'sellingCountries', []);
      const productState = _get(productsPerCountry, `${country}.${product.key}`, []);

      const ProductChannelCheckbox = ({ channel }) => (
        <ChannelCheckbox
          onClickFn={this.onClickCheckbox}
          productKey={product.key}
          channel={channel}
          checked={productState.includes(channel)}
        >
          {this.props.t(`${tPrefix}.channels.${channel}`)}
        </ChannelCheckbox>
      );

      if (sellingCountries.includes(country)) {
        all.push({
          value: product.key,
          key: product.key,
          label: product.label,
          isChecked: productState.length > 0,
          rightIcon: (
            <div
              style={{
                top: -21,
                height: 60,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    paddingRight: 30,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  {productState.length > 0 && (
                    <ActionIconCheckmark
                      height="35"
                      width="35"
                      viewBox="0 0 20 20"
                      colorToken="colors/text/accent"
                    />
                  )}
                </div>
                <div style={{ textAlign: 'left', paddingRight: 70 }}>
                  <ProductChannelCheckbox channel="ONLINE" />
                  <SpacerVertical spaceToken="space/100" />
                  <ProductChannelCheckbox channel="IN_STORE" />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <ProductChannelCheckbox channel="TELESALES" />
                  <SpacerVertical spaceToken="space/100" />
                  <ProductChannelCheckbox channel="SELLER_APP" />
                </div>
              </div>
            </div>
          ),
          ...(productState.length > 0
            ? generateCyElemId(SELECTED_PRODUCT_BATCH(country), window)
            : {}),
          ...generateCyElemId(SELECT_PRODUCT(country, product.key), window),
        });
      }
      return all;
    }, []);
  }

  onNext() {
    const { productsPerCountry } = this.state;
    let hasEmptyCountries = false;

    const body = {
      products: Object.keys(productsPerCountry).map(country => {
        if (Object.keys(productsPerCountry[country]).length === 0) {
          hasEmptyCountries = true;
        }

        return {
          country,
          products: Object.keys(productsPerCountry[country]).map(product => {
            if (productsPerCountry[country][product].length === 0) {
              hasEmptyCountries = true;
            }

            return {
              product,
              channels: productsPerCountry[country][product],
            };
          }),
        };
      }),
    };

    if (hasEmptyCountries) {
      this.setState({
        ...this.state,
        hasEmptyCountries: true,
      });
    } else {
      const payload = payloadUtils.getMerchantCapabilityPayload(
        this.props.mid,
        this.state,
        () => body,
        'PreAssessmentProductsContainer',
      );
      this.props.onNext(payload);
    }
  }

  onSelect(selectedKey, event) {
    const { currentCountry } = this.state;
    const { [selectedKey]: currentProduct, ...currentProducts } = _get(
      this.state,
      `productsPerCountry.${currentCountry}`,
      {},
    );

    this.setState({
      productsPerCountry: {
        ...this.state.productsPerCountry,
        [currentCountry]: {
          ...currentProducts,
          ...(currentProduct && currentProduct.length > 0 ? {} : { [selectedKey]: ['ONLINE'] }),
        },
      },
    });
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t } = this.props;
    const { sellingCountries = { all: [] } } = this.props.preAssessmentDetails;
    const { currentCountry, hasEmptyCountries } = this.state;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        contentWidth={12}
        error={error}
      >
        {hasEmptyCountries && <Alert type="error" content={t(`${tPrefix}.errors.invalid`)} />}

        <SpacerVertical spaceToken="space/150" />
        <Row additionalStyles={{ borderTop: '1px solid rgb(216, 216, 216)' }}>
          <Column numberOfColumns={2}>
            <SelectorLogo
              onSelect={c => this.onChangeCountry(c)}
              options={sellingCountries.all.map(c => ({
                key: c,
                value: c,
                label: c,
                pressed: currentCountry === c,
                logoProps: {
                  icon: () => <Flag country={c} height={50} width={50} viewBox="0 0 180 180" />,
                },
                ...generateCyElemId(SELECT_COUNTRY(c), window),
              }))}
            />
          </Column>
          <Column numberOfColumns={6}>
            {/* We need to add this padding & margin to compensate bubble-ui's negative margin */}
            <div
              style={{
                paddingLeft: 20,
                marginLeft: 20,
                borderLeft: '1px solid rgb(216, 216, 216)',
              }}
            >
              <SelectorDirect
                onSelect={this.onSelect}
                name="options-regular"
                options={this.getOptions(currentCountry)}
              />
            </div>
          </Column>
        </Row>
        <SpacerVertical spaceToken="space/600" />
        <Row>
          <Column numberOfColumns={8}>
            <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
              {hasBack && (
                <>
                  <BackButton
                    onClick={onBack}
                    disabled={isDisabled}
                    {...generateCyElemId(BTN_BACK, window)}
                  >
                    {t(`${tPrefix}.buttons.back`)}
                  </BackButton>
                  <SpacerHorizontal spaceToken="space/300" />
                </>
              )}
              <CallToActionButton
                loading={isLoading}
                onClick={this.onNext}
                disabled={isDisabled}
                {...generateCyElemId(BTN_SUBMIT, window)}
              >
                {t(`${tPrefix}.buttons.next`)}
              </CallToActionButton>
            </HorizontalWrapper>
          </Column>
        </Row>
      </ContainerTemplate>
    );
  }
}

PreAssessmentProducts.propTypes = {
  ...defaultContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  defaultState: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default compose(
  connect(state => ({
    countries: getSellingCountries(state),
    productNames: getProductNames(state),
    defaultState: getDefaultState(state),
    preAssessmentDetails: preAssessmentDetails(state),
    t: translator(state),
  })),
  withCapabilityBeforeNext({ cap: CAPS.V2_PRE_ASSESSMENT_PATCH }),
)(PreAssessmentProducts);
